<template>
  <span
    class="tag is-uppercase is-light mr-1"
    :title="worker.type"
    :style="style"
  >
    {{ name }}
  </span>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash'
import { type CSSProperties, computed } from 'vue'

import { DEFAULT_WORKER_TYPE_COLOR } from '@/config'
import { isLightColor } from '@/helpers'
import { useAuthStore, useWorkerTypeStore } from '@/stores'
import type { Worker } from '@/types/worker'

const { worker } = defineProps<{ worker: Worker }>()

const authStore = useAuthStore()
const workerTypeStore = useWorkerTypeStore()

// On mount, list all worker types immediately
if (authStore.isVerified && isEmpty(workerTypeStore.workerTypes)) workerTypeStore.list()

const workerType = computed(() => {
  // The worker types may not have been listed
  if (!workerTypeStore.workerTypes) return null
  /*
   * Even when the worker types have been listed, they might not include the type for this worker,
   * because for non-admins, only the types from workers accessible to the user will be included.
   */
  return Object.values(workerTypeStore.workerTypes).find(({ slug }) => slug === worker.type) || null
})

const name = computed((): string => {
  return workerType.value?.display_name ?? worker.type
})

const style = computed((): CSSProperties => {
  let color = workerType.value?.color ?? DEFAULT_WORKER_TYPE_COLOR
  if (!color.startsWith('#')) color = `#${color}`
  return { 'background-color': color, color: isLightColor(color) ? 'black' : 'white' }
})
</script>
